import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useNavigate, Link } from 'react-router-dom';
import Logo from '../assets/Logo.svg';
import SignUpImage from '../assets/Signup.svg';
import { forgotPassword, resetPassword } from '../api/api';
import { Eye, EyeOff } from 'lucide-react';
import FooterAuth from '../components/layout/FooterAuth';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);

  // countdown for resend
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleSendOtp = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error('Email is required');
      return;
    }

    setLoading(true);

    try {
      const res = await forgotPassword(email.toLowerCase());
      toast.success(res?.message || 'OTP sent to your email');
      setStep(2);
      setCountdown(60);
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to send OTP';

      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error('Please enter 6-digit OTP');
      return;
    }
    if (!newPassword.trim()) {
      toast.error('Please enter a new password');
      return;
    }

    setLoading(true);
    try {
      const res = await resetPassword(email.toLowerCase(), otp, newPassword);
      toast.success(res?.message || 'Password reset successful');
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col min-h-screen w-full font-sans bg-gray-50 overflow-x-hidden"
      >
        <div class="flex flex-col md:flex-row flex-grow">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full md:w-1/2 flex flex-col bg-white shadow-lg relative px-8 md:px-20 py-12"
          >
            {/* Logo */}
            <div className="absolute top-6 left-4 md:left-10">
              <a href="/">
                <img src={Logo} alt="Legal AI Logo" className="w-20 h-auto" />
              </a>
            </div>

            <div className="w-full max-w-md mx-auto pt-16">
              <h1 className="text-3xl font-bold text-center mb-6">
                {step === 1 ? 'Forgot Password' : 'Reset Password'}
              </h1>

              {step === 1 ? (
                // Step 1: Enter email
                <form onSubmit={handleSendOtp} className="space-y-4">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="w-full border border-gray-300 rounded px-4 py-2 focus:ring-2 focus:ring-brand"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-brandDark to-brand text-white py-2 rounded-lg shadow-md"
                  >
                    {loading ? 'Sending...' : 'Send OTP'}
                  </motion.button>
                </form>
              ) : (
                // Step 2: Enter OTP + new password
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <p className="text-sm text-gray-600 text-center">
                    Enter the 6-digit OTP sent to <b>{email}</b>
                  </p>

                  {/* OTP input */}
                  <div className="flex justify-center gap-3 mb-4">
                    {Array.from({ length: 6 }).map((_, idx) => (
                      <motion.input
                        key={idx}
                        type="text"
                        maxLength="1"
                        className="w-12 h-12 text-center text-xl font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand"
                        whileFocus={{ scale: 1.1 }}
                        value={otp[idx] || ''}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          if (!val) return;

                          setOtp((prev) => {
                            const chars = prev.split('');
                            chars[idx] = val;
                            return chars.join('');
                          });

                          if (e.target.nextSibling) {
                            e.target.nextSibling.focus();
                          }
                        }}
                        onPaste={(e) => {
                          e.preventDefault();
                          const pasteData = e.clipboardData
                            .getData('text')
                            .replace(/[^0-9]/g, '')
                            .slice(0, 6);

                          if (!pasteData) return;

                          setOtp((prev) => {
                            const chars = prev.split('');
                            for (let i = 0; i < 6; i++) {
                              chars[i] = pasteData[i] || chars[i] || '';
                            }
                            return chars.join('');
                          });

                          const inputs =
                            e.currentTarget.parentNode.querySelectorAll(
                              'input'
                            );
                          pasteData.split('').forEach((char, i) => {
                            if (inputs[i]) inputs[i].value = char;
                          });

                          if (inputs[Math.min(pasteData.length, 5)]) {
                            inputs[Math.min(pasteData.length, 5)].focus();
                          }
                        }}
                      />
                    ))}
                  </div>

                  {/* New Password */}
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="New Password"
                      className="w-full border border-gray-300 rounded px-4 py-2 pr-10 focus:ring-2 focus:ring-brand"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  {/* Resend OTP */}
                  <div className="text-center mb-2">
                    {countdown > 0 ? (
                      <span className="text-sm text-gray-500">
                        Resend OTP in {countdown}s
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        className="text-sm font-semibold text-brand"
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-brandDark to-brand text-white py-2 rounded-lg shadow-md"
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </motion.button>
                </form>
              )}

              <p className="mt-6 text-sm text-center">
                Remembered your password?{' '}
                <Link
                  to="/login"
                  className="font-semibold text-black underline"
                >
                  Back to Login
                </Link>
              </p>
            </div>
          </motion.div>

          {/* Right Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full md:w-1/2 flex items-center justify-center bg-[#DADDFB] overflow-hidden"
          >
            <img
              src={SignUpImage}
              alt="Forgot Password Illustration"
              className="max-w-[80%] max-h-[80%] object-contain"
            />
          </motion.div>
        </div>

        {/* Footer */}
      </motion.div>
      <FooterAuth />
    </>
  );
}
